import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ColorPicker, Input, InputNumber, Radio } from "antd";
import { Align, EmailBlock } from "./emailBuilder.model";
import { EmailTemplateService } from "../../services/email-template.service";
import useAppDispatch from "../../hooks/useAppDispatch";
import { handleError } from "../../helper/errorHandler";
import styles from "./EmailBuilder.module.scss";

interface IBlockSettingsProps {
  block: EmailBlock;
  pathKey: string;
  constants: string[];
  onChange: (block: EmailBlock) => void;
  // The text of a heading / paragraph changed through the toolbar (already in the DOM)
  onTextCommand: () => void;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className={styles.field}>
    <span>{label}</span>
    {children}
  </label>
);

// Keeps the caret in the text - a mousedown on the toolbar would otherwise take the focus away
const keepSelection = (e: React.MouseEvent) => e.preventDefault();

const BlockSettings = ({
  block,
  pathKey,
  constants,
  onChange,
  onTextCommand,
}: IBlockSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fileRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const set = (changes: Partial<EmailBlock>) => onChange({ ...block, ...changes } as EmailBlock);

  const isTextFocused = () => document.activeElement?.getAttribute("data-rich-text") === pathKey;

  const runTextCommand = (command: string, value?: string) => {
    if (!isTextFocused()) return;
    document.execCommand(command, false, value);
    onTextCommand();
  };

  const addLink = () => {
    if (!isTextFocused()) return;
    const url = window.prompt(t("EnterLinkUrl"));
    if (url) runTextCommand("createLink", url);
  };

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploading(true);
    try {
      const res = await EmailTemplateService.uploadEmailImage(file);
      set({ url: res.data.url } as Partial<EmailBlock>);
    } catch (err) {
      handleError(err, dispatch, t);
    } finally {
      setIsUploading(false);
    }
  };

  const colorField = (label: string, value: string, key: string) => (
    <Field label={label}>
      <ColorPicker
        value={value}
        showText
        onChangeComplete={(color) => set({ [key]: color.toHexString() } as Partial<EmailBlock>)}
      />
    </Field>
  );

  const numberField = (label: string, value: number, key: string, min = 0, max = 600) => (
    <Field label={label}>
      <InputNumber
        min={min}
        max={max}
        value={value}
        onChange={(v) => set({ [key]: v ?? min } as Partial<EmailBlock>)}
      />
    </Field>
  );

  const alignField = (value: Align) => (
    <Field label={t("Alignment")}>
      <Radio.Group
        value={value}
        optionType="button"
        onChange={(e) => set({ align: e.target.value } as Partial<EmailBlock>)}
        options={[
          { value: "left", label: t("Left") },
          { value: "center", label: t("Center") },
          { value: "right", label: t("Right") },
        ]}
      />
    </Field>
  );

  // A constant is appended to a link field; in a text it goes to the caret
  const constantChips = (onPick: (constant: string) => void) => (
    <div className={styles.constants}>
      {constants.map((constant) => (
        <button
          key={constant}
          type="button"
          onMouseDown={keepSelection}
          onClick={() => onPick(constant)}
        >
          {constant}
        </button>
      ))}
    </div>
  );

  const backgroundField = colorField(
    t("BackgroundColor"),
    block.backgroundColor || "#ffffff00",
    "backgroundColor"
  );

  switch (block.type) {
    case "heading":
    case "paragraph":
      return (
        <div className={styles.settings}>
          <p className={styles.hint}>{t("EmailBuilderTextHint")}</p>
          <div className={styles.textToolbar}>
            <button
              type="button"
              onMouseDown={keepSelection}
              onClick={() => runTextCommand("bold")}
            >
              <b>B</b>
            </button>
            <button
              type="button"
              onMouseDown={keepSelection}
              onClick={() => runTextCommand("italic")}
            >
              <i>I</i>
            </button>
            <button
              type="button"
              onMouseDown={keepSelection}
              onClick={() => runTextCommand("underline")}
            >
              <u>U</u>
            </button>
            <button type="button" onMouseDown={keepSelection} onClick={addLink}>
              {t("Link")}
            </button>
            <button
              type="button"
              onMouseDown={keepSelection}
              onClick={() => runTextCommand("unlink")}
            >
              {t("RemoveLink")}
            </button>
          </div>
          <Field label={t("Constants")}>
            {constantChips((constant) => runTextCommand("insertText", constant))}
          </Field>
          {numberField(t("FontSize"), block.fontSize, "fontSize", 8, 72)}
          {colorField(t("TextColor"), block.color, "color")}
          {alignField(block.align)}
          {numberField(t("Padding"), block.padding, "padding", 0, 80)}
          {backgroundField}
        </div>
      );
    case "image":
      return (
        <div className={styles.settings}>
          <Field label={t("Image")}>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif"
              style={{ display: "none" }}
              onChange={uploadImage}
            />
            <Button loading={isUploading} onClick={() => fileRef.current?.click()}>
              {t("UploadImage")}
            </Button>
          </Field>
          <Field label={t("ImageUrl")}>
            <Input value={block.url} onChange={(e) => set({ url: e.target.value })} />
          </Field>
          <Field label={t("AltText")}>
            <Input value={block.alt} onChange={(e) => set({ alt: e.target.value })} />
          </Field>
          <Field label={t("LinkOptional")}>
            <Input value={block.link} onChange={(e) => set({ link: e.target.value })} />
            {constantChips((constant) => set({ link: block.link + constant }))}
          </Field>
          {numberField(t("Width"), block.width, "width", 20, 552)}
          {alignField(block.align)}
          {numberField(t("Padding"), block.padding, "padding", 0, 80)}
          {backgroundField}
        </div>
      );
    case "button":
      return (
        <div className={styles.settings}>
          <Field label={t("ButtonText")}>
            <Input value={block.text} onChange={(e) => set({ text: e.target.value })} />
          </Field>
          <Field label={t("Link")}>
            <Input value={block.link} onChange={(e) => set({ link: e.target.value })} />
            {constantChips((constant) => set({ link: block.link + constant }))}
          </Field>
          {colorField(t("ButtonColor"), block.bgColor, "bgColor")}
          {colorField(t("TextColor"), block.textColor, "textColor")}
          {alignField(block.align)}
          {numberField(t("BorderRadius"), block.borderRadius, "borderRadius", 0, 40)}
          {numberField(t("Padding"), block.padding, "padding", 4, 40)}
          {backgroundField}
        </div>
      );
    case "divider":
      return (
        <div className={styles.settings}>
          {colorField(t("Color"), block.color, "color")}
          {numberField(t("Thickness"), block.thickness, "thickness", 1, 10)}
          {numberField(t("WidthPercent"), block.widthPercent, "widthPercent", 10, 100)}
          {numberField(t("Margin"), block.margin, "margin", 0, 80)}
          {backgroundField}
        </div>
      );
    case "spacer":
      return (
        <div className={styles.settings}>
          {numberField(t("Height"), block.height, "height", 4, 200)}
          {backgroundField}
        </div>
      );
    case "columns":
      return (
        <div className={styles.settings}>
          <p className={styles.hint}>{t("EmailBuilderColumnsHint")}</p>
          {block.columns.length > 1 &&
            block.columns.map((column, i) => (
              <Field key={i} label={t("ColumnWidthPercent", { number: i + 1 })}>
                <InputNumber
                  min={10}
                  max={90}
                  value={column.widthPercent}
                  onChange={(v) =>
                    set({
                      columns: block.columns.map((c, j) =>
                        j === i ? { ...c, widthPercent: v ?? 10 } : c
                      ),
                    })
                  }
                />
              </Field>
            ))}
          {backgroundField}
        </div>
      );
  }
};

export default BlockSettings;
